import { GenericFilter } from '@/comps/FilterPicker/FilterPicker';
import { Calendar } from '@/comps/primitives/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/comps/primitives/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/comps/primitives/tabs';
import { useGlobalFiltersContext } from '@/contexts/filters/filter-context';
import { FilterColumnValue, FILTERS } from '@/models/common/filters';
import { useProjectsContext } from '@/providers/projects-provider';
import { formatToHuman } from '@/utils/date.util';
import { Button } from '@/comps/primitives/button';
import { Settings2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { FilterContentV2NestedFilterRow } from '@/comps/FilterPicker/filter-content-v2/FilterContentV2NestedFilterRow';
import { buildFilterKindName } from '@/comps/FilterPicker/utils';
import { TimeRangeConfig } from '@/api/generated/model';

const NESTED_POPOVER_CONTENT_CLASS = 'z-120 border-zinc-800 bg-[#09090B]';

export default function SpecialFilterPopover({
  date,
  setDate,
  filters,
  setFilters,
  timeRange,
}: {
  date: Date | null;
  setDate: (date: Date | null) => void;
  filters: GenericFilter[];
  setFilters: (filters: GenericFilter[]) => void;
  timeRange?: TimeRangeConfig;
}) {
  const availableFilterKinds = useMemo(() => {
    const baseFilters: (FilterColumnValue | string)[] = [
      FILTERS.columnCohort,
      FILTERS.columnIntent,
      FILTERS.columnTopic,
      FILTERS.columnDataSource,
      FILTERS.columnLanguage,
      FILTERS.columnUser,
      FILTERS.columnPii,
      FILTERS.columnFeedbackTypeV2,
      FILTERS.columnSentiment,
      FILTERS.columnEmotions,
    ];
    return baseFilters;
  }, []);

  const { selectedProject } = useProjectsContext();
  const { timeRange: globalTimeRange } = useGlobalFiltersContext();
  const effectiveTimeRange = timeRange ?? globalTimeRange;
  const [popoverOpen, setPopoverOpen] = useState(false);
  const projectId = selectedProject?.id ?? '';

  const customFilter = filters.length > 0 ? filters[0] : null;

  const triggerLabel = date
    ? formatToHuman(date.valueOf())
    : customFilter
      ? `${buildFilterKindName(customFilter.column)}: ${customFilter.values.join(', ')}`
      : 'Choose filter options';

  return (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverTrigger asChild>
        <Button variant={'outline'} size="sm" className="border-zinc-800 bg-[#09090B] text-xs text-white">
          <Settings2 size={12} className="mr-1.5" />
          <div className="max-w-45 overflow-hidden text-ellipsis whitespace-nowrap">{triggerLabel}</div>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        avoidCollisions={false}
        onOpenAutoFocus={(event) => event.preventDefault()}
        className={`${NESTED_POPOVER_CONTENT_CLASS} w-auto p-2`}
      >
        <Tabs
          onValueChange={(value) => {
            if (value === 'date') {
              setFilters([]);
            } else {
              setDate(null);
            }
          }}
          defaultValue={customFilter ? 'custom' : 'date'}
          className="w-[320px]"
        >
          <TabsList className="mb-2 grid w-full grid-cols-2">
            <TabsTrigger value="date">Date filter</TabsTrigger>
            <TabsTrigger value="custom">Custom filter</TabsTrigger>
          </TabsList>
          <TabsContent value="date" className="mx-auto! flex-row">
            <Calendar
              mode="single"
              selected={date ?? undefined}
              onSelect={(d) => {
                setDate(d ?? null);
                setFilters([]);
                setPopoverOpen(false);
              }}
              initialFocus
              className="mx-auto flex w-full justify-center"
            />
          </TabsContent>
          <TabsContent value="custom" className="flex flex-col gap-2 px-1 py-1">
            <FilterContentV2NestedFilterRow
              filter={customFilter}
              onChange={(nextFilter) => {
                setDate(null);
                setFilters(nextFilter ? [nextFilter] : []);
              }}
              fieldKinds={availableFilterKinds}
              projectId={projectId}
              timeRange={effectiveTimeRange}
              volumeKind="users"
              onValueApplied={() => setPopoverOpen(false)}
            />
          </TabsContent>
        </Tabs>
      </PopoverContent>
    </Popover>
  );
}
